import { useRef, useState } from 'react';
import applicationConfig from '../../config';
import type { FormConfig } from '../../config/types';
import { updateForm } from '../../service/form';
import type { FetchFormResponse } from '../../service/types';
import { extractConditionalQuestions } from '../../utils/config-utils';
import { getQuarterCompletion } from './quarter-completion';
import ScsQuarterCompletionModal from './ScsQuarterCompletionModal';

type Props = {
  enabled: boolean;
  formId: string;
  config: FormConfig;
  subsection: string;
  getToken: () => Promise<string>;
};

export function useScsQuarterCompletion({ enabled, formId, config, subsection, getToken }: Props) {
  const [pending, setPending] = useState<{
    completion: NonNullable<ReturnType<typeof getQuarterCompletion>>;
    data: FetchFormResponse;
  } | null>(null);
  const resolve = useRef<((path?: string) => void) | null>(null);
  const close = (path?: string) => {
    setPending(null);
    resolve.current?.(path);
    resolve.current = null;
  };
  const afterSave = async (data?: FetchFormResponse) => {
    if (!enabled || !data) return;
    const completion = getQuarterCompletion(config, data, subsection);
    if (!completion) return;
    return new Promise<string | undefined>((done) => {
      resolve.current = done;
      setPending({ completion, data });
    });
  };
  const onDecision = async (send: boolean) => {
    if (!pending) return;
    const { completion, data } = pending;
    if (send) {
      close(
        `/scs/${applicationConfig.urls.summary}?check_answers=1&formId=${encodeURIComponent(formId)}`,
      );
      return;
    } else {
      const token = await getToken();
      const page = completion.lastPage;
      const questions = extractConditionalQuestions(('questions' in page && page.questions) || []);
      const answers = Object.fromEntries(
        questions
          .filter((question) => data.answers?.[question.name] !== undefined)
          .map((question) => [question.name, data.answers![question.name]]),
      );
      await updateForm(
        token,
        {
          'subsection-id': completion.lastSubsection,
          'page-id': page.name,
          'is-save-unfinished': true,
          'defer-scs-approval': true,
          answers,
        },
        { form_id: formId },
      );
    }
    close();
  };
  return {
    afterSave,
    modal: pending && (
      <ScsQuarterCompletionModal
        quarter={pending.completion.quarter}
        missing={pending.completion.missing}
        onDecision={onDecision}
        onMissingClose={() => close()}
      />
    ),
  };
}
